import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// ── Dados dos slides ──────────────────────────────────────────
const SLIDES = [
  {
    id: 1,
    type: 'capa',
  },
  {
    id: 2,
    type: 'dark',
    label: 'O Problema',
    title: ['Um orçamento ', 'consome dias', ' de trabalho manual.'],
    titleOrange: [false, true, false],
    desc: 'Levantar quantitativos de uma obra representa 80% do tempo total de orçamentação. Engenheiros e construtores desperdiçam horas em planilhas propensas a erros — tempo que deveria ir para projetos e clientes.',
    cards: [
      { icon: '⏱️', title: 'Dias de trabalho', text: 'Orçamentos manuais levam de 2 a 5 dias úteis para serem concluídos' },
      { icon: '❌', title: 'Erros frequentes', text: 'Planilhas manuais geram inconsistências e retrabalho constante' },
      { icon: '💸', title: 'Custo alto', text: 'Horas de engenheiro pagas para tarefas repetitivas e mecânicas' },
    ],
    bigNumber: '80%',
  },
  {
    id: 3,
    type: 'light',
    label: 'Mercado-Alvo',
    title: ['Quem sente ', 'essa dor', ' todo dia?'],
    titleOrange: [false, true, false],
    desc: 'Um mercado amplo com necessidade urgente e não atendida',
    cards: [
      { icon: '🏗️', title: 'Construtores', text: 'Precisam orçar rápido para fechar contratos e apresentar propostas competitivas' },
      { icon: '⚙️', title: 'Engenheiros Autônomos', text: 'Trabalham com múltiplos clientes e precisam de agilidade sem perder precisão' },
      { icon: '📐', title: 'Arquitetos', text: 'Precisam apresentar estimativas de custo junto com o projeto para aprovação' },
      { icon: '🎓', title: 'Estudantes e Recém-formados', text: 'Aprendem na prática e precisam de uma ferramenta acessível e confiável' },
    ],
    grid: '2x2',
  },
  {
    id: 4,
    type: 'dark',
    label: 'A Solução',
    title: ['IA que ', 'lê sua planta', '\ne faz o orçamento.'],
    titleOrange: [false, true, false],
    desc: 'Sobe a planta baixa. A IA identifica cômodos, áreas, esquadrias e acabamentos automaticamente. Orçamento CUB ou SINAPI gerado em minutos — não em dias.',
    cards: [
      { icon: '🧠', title: 'Visão Computacional', text: 'Claude Vision lê e interpreta plantas baixas em PDF ou PNG com alta precisão' },
      { icon: '📊', title: 'SINAPI + CUB Oficiais', text: '30.000+ composições SINAPI indexadas. Dados atualizados mensalmente pelo governo' },
      { icon: '📄', title: 'Exportação Profissional', text: 'PDF e Excel prontos para apresentar ao cliente, banco ou incorporadora' },
    ],
    highlighted: true,
  },
  {
    id: 5,
    type: 'bruno',
  },
  {
    id: 6,
    type: 'steps',
    label: 'Como Funciona',
    title: ['', '3 passos', ' para o orçamento completo.'],
    titleOrange: [false, true, false],
    desc: 'Simples para o usuário, poderoso por dentro.',
    steps: [
      { icon: '📤', title: 'Sobe a planta', text: 'PDF ou PNG da planta baixa. A IA aceita plantas cotadas, humanizadas ou apenas com escala definida.' },
      { icon: '🧠', title: 'IA analisa e extrai', text: 'Claude Vision identifica cômodos, áreas, esquadrias e acabamentos. O usuário revisa e ajusta em segundos.' },
      { icon: '📄', title: 'Recebe o orçamento', text: 'PDF ou Excel com orçamento detalhado por etapa de obra — fundação, estrutura, alvenaria, acabamentos e mais.' },
    ],
  },
  {
    id: 7,
    type: 'demo',
  },
  {
    id: 8,
    type: 'stats',
    label: 'O que já temos',
    title: ['Produto ', 'funcional', ',\npronto para o mercado.'],
    titleOrange: [false, true, false],
    stats: [
      { value: '27', label: 'estados brasileiros cobertos', highlight: true },
      { value: '30K+', label: 'composições SINAPI indexadas', highlight: true },
      { value: '✅ Em produção', label: 'Fluxo completo validado e funcionando', highlight: false },
      { value: '🚀 Pré-lançamento', label: 'Primeiros usuários em validação do fluxo', highlight: false },
    ],
  },
  {
    id: 9,
    type: 'market',
    label: 'Mercado',
    title: ['Um mercado ', 'enorme', '\nsem solução adequada.'],
    titleOrange: [false, true, false],
  },
  {
    id: 10,
    type: 'pricing',
    label: 'Modelo de Negócio',
    title: ['Margem acima de ', '90%', '.\nCusto de IA abaixo de R$1.'],
    titleOrange: [false, true, false],
  },
  {
    id: 11,
    type: 'whynow',
    label: 'Por que Agora',
    title: ['A janela de\n', 'oportunidade está aberta', '.'],
    titleOrange: [false, true, false],
    cards: [
      { icon: '🤖', title: 'IA Generativa Madura', text: 'Claude Vision tornou viável, pela primeira vez, ler e interpretar plantas baixas automaticamente com alta precisão.' },
      { icon: '🏆', title: 'Sem Concorrência Direta', text: 'Nenhum produto brasileiro combina leitura de plantas + assistente IA + SINAPI/CUB em um único fluxo.' },
      { icon: '📈', title: 'Mercado em Crescimento', text: 'Adoção de tecnologia na construção civil acelerou pós-pandemia. O setor está pronto para inovar.' },
      { icon: '⚡', title: 'Momento do Lançamento', text: 'Produto funcional, infraestrutura pronta e canal de distribuição a definir. O próximo passo é crescimento.' },
    ],
    grid: '2x2',
  },
  {
    id: 12,
    type: 'cta',
  },
];

// ── Sub-componentes de slide ──────────────────────────────────

function Logo({ white = false }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-black text-sm"
           style={{ background: '#F97316', fontFamily: 'system-ui' }}>qco</div>
      <span className={`font-extrabold text-lg tracking-tight ${white ? 'text-white' : 'text-[#1E3A5F]'}`}
            style={{ fontFamily: 'system-ui' }}>QuantoCustaObra</span>
    </div>
  );
}

function SlideHeader({ label, white = false }) {
  return (
    <div className="flex items-center justify-between mb-10">
      <Logo white={white} />
      <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#F97316' }}>{label}</span>
    </div>
  );
}

function TitleText({ parts, orangeMap, white = false }) {
  return (
    <h2 className={`font-black leading-tight mb-3 whitespace-pre-line`}
        style={{ fontSize: 'clamp(28px, 4vw, 52px)', letterSpacing: '-1.5px', fontFamily: 'system-ui' }}>
      {parts.map((p, i) =>
        orangeMap[i]
          ? <span key={i} style={{ color: '#F97316' }}>{p}</span>
          : <span key={i} style={{ color: white ? 'white' : '#1E3A5F' }}>{p}</span>
      )}
    </h2>
  );
}

// ── Slides individuais ────────────────────────────────────────

function SlideCapa() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden"
         style={{ background: '#0F1F33' }}>
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: 'radial-gradient(ellipse at 75% 20%, rgba(249,115,22,0.15) 0%, transparent 60%)' }} />
      <div className="inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-7"
           style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.4)', color: '#F97316' }}>
        🏗️ Apresentação do Produto • 2026
      </div>
      <div className="flex items-center gap-3 mb-8">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-lg"
             style={{ background: '#F97316', fontFamily: 'system-ui' }}>qco</div>
        <span className="font-black text-3xl text-white tracking-tight" style={{ fontFamily: 'system-ui' }}>QuantoCustaObra</span>
      </div>
      <h1 className="font-black text-white leading-tight mb-3 tracking-tight"
          style={{ fontSize: 'clamp(44px, 8vw, 88px)', letterSpacing: '-3px', fontFamily: 'system-ui' }}>
        Orçamento de Obra<br /><span style={{ color: '#F97316' }}>em 5 Minutos.</span>
      </h1>
      <p className="mb-8" style={{ fontSize: 18, color: 'rgba(255,255,255,0.55)' }}>IA que lê sua planta e gera o orçamento completo.</p>
      <div className="px-6 py-3 rounded-full text-sm"
           style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}>
        Baseado no SINAPI e CUB Oficiais &nbsp;•&nbsp; 27 estados cobertos
      </div>
    </div>
  );
}

function SlideCards({ slide }) {
  const isDark = slide.type === 'dark';
  const bg = isDark ? '#0A1628' : '#F8F9FA';
  const cardBg = isDark ? 'rgba(255,255,255,0.05)' : 'white';
  const cardBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E9ECEF';
  const descColor = isDark ? 'rgba(255,255,255,0.55)' : '#6B7280';

  return (
    <div className="w-full h-full flex flex-col p-14 relative overflow-hidden" style={{ background: bg }}>
      {isDark && <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 80% 10%, rgba(249,115,22,0.07) 0%, transparent 60%)' }} />}
      <SlideHeader label={slide.label} white={isDark} />
      <TitleText parts={slide.title} orangeMap={slide.titleOrange} white={isDark} />
      <p className="mb-8 text-base leading-relaxed" style={{ color: descColor, maxWidth: 580 }}>{slide.desc}</p>
      <div className={`flex-1 grid gap-4 ${slide.grid === '2x2' ? 'grid-cols-2' : 'grid-cols-3'}`} style={{ alignContent: 'start' }}>
        {slide.cards.map((c, i) => (
          <div key={i} className="rounded-2xl p-5 flex flex-col"
               style={{ background: cardBg, border: `1px solid ${slide.highlighted ? 'rgba(249,115,22,0.3)' : cardBorder}`,
                        boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl mb-3"
                 style={{ background: 'rgba(249,115,22,0.15)' }}>{c.icon}</div>
            <div className="font-bold text-sm mb-1" style={{ color: isDark ? 'white' : '#1E3A5F' }}>{c.title}</div>
            <div className="text-xs leading-relaxed" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6B7280' }}>{c.text}</div>
          </div>
        ))}
      </div>
      {slide.bigNumber && (
        <div className="absolute right-14 top-1/2 -translate-y-1/2 font-black pointer-events-none select-none"
             style={{ fontSize: 'clamp(100px,18vw,200px)', color: '#F97316', opacity: 0.1, letterSpacing: '-8px', lineHeight: 1, fontFamily: 'system-ui' }}>
          {slide.bigNumber}
        </div>
      )}
    </div>
  );
}

function SlideBruno() {
  return (
    <div className="w-full h-full flex flex-col p-14 relative overflow-hidden" style={{ background: '#0A1628' }}>
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: 'radial-gradient(ellipse at 80% 10%, rgba(249,115,22,0.07) 0%, transparent 60%)' }} />
      <SlideHeader label="Diferencial Único" white />
      <div className="flex-1 grid grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wide mb-4"
               style={{ background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.3)', color: '#F97316' }}>
            🤖 Assistente IA
          </div>
          <h2 className="font-black text-white leading-tight mb-4"
              style={{ fontSize: 'clamp(28px,4vw,52px)', letterSpacing: '-1.5px', fontFamily: 'system-ui' }}>
            Conheça o <span style={{ color: '#F97316' }}>Bruno</span>.
          </h2>
          <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Bruno guia o usuário por todo o processo de orçamentação via chat conversacional. Responde dúvidas, valida dados e explica cada etapa — como ter um engenheiro de orçamentos disponível 24/7.
          </p>
          <div className="rounded-xl p-4" style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)' }}>
            <div className="font-bold text-sm mb-1" style={{ color: '#F97316' }}>🏆 Diferencial único no Brasil</div>
            <div className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Nenhum concorrente brasileiro tem assistente IA conversacional integrado ao fluxo de orçamentação.
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          {[
            { user: true, text: '"Tenho uma casa de 120m², padrão médio, em MG. Quanto vai custar?"' },
            { user: false, text: '"Ótimo! Vou calcular seu orçamento pelo CUB/MG. Você tem a planta baixa? Se sim, sobe o arquivo que eu extraio as áreas automaticamente."' },
            { user: true, text: '"Sim, aqui está o PDF da planta."' },
          ].map((m, i) => (
            <div key={i} className={`rounded-2xl p-4 ${m.user ? '' : 'ml-6'}`}
                 style={{
                   background: m.user ? 'rgba(255,255,255,0.06)' : 'rgba(249,115,22,0.1)',
                   borderLeft: `3px solid #F97316`
                 }}>
              <div className="text-xs mb-2" style={{ color: m.user ? 'rgba(255,255,255,0.4)' : '#F97316' }}>
                {m.user ? '👤 Usuário' : '🤖 Bruno'}
              </div>
              <div className="text-sm leading-relaxed" style={{ color: m.user ? 'white' : 'rgba(255,255,255,0.85)' }}>{m.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SlideSteps({ slide }) {
  return (
    <div className="w-full h-full flex flex-col p-14" style={{ background: '#F8F9FA' }}>
      <SlideHeader label={slide.label} />
      <TitleText parts={slide.title} orangeMap={slide.titleOrange} />
      <p className="mb-8 text-base" style={{ color: '#6B7280' }}>{slide.desc}</p>
      <div className="flex-1 flex flex-col gap-4 justify-center">
        {slide.steps.map((s, i) => (
          <div key={i} className="flex items-center gap-5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-base shrink-0"
                 style={{ background: '#F97316', fontFamily: 'system-ui' }}>{i + 1}</div>
            <div className="flex-1 rounded-2xl p-5" style={{ background: 'white', border: '1px solid #E9ECEF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div className="font-bold mb-1" style={{ color: '#1E3A5F', fontSize: 17 }}>{s.icon} {s.title}</div>
              <div className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{s.text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideDemo() {
  return (
    <div className="w-full h-full flex flex-col p-14" style={{ background: '#0A1628' }}>
      <SlideHeader label="Demo ao Vivo" white />
      <h2 className="font-black text-white mb-8" style={{ fontSize: 'clamp(28px,4vw,52px)', letterSpacing: '-1.5px', fontFamily: 'system-ui' }}>
        Ver para <span style={{ color: '#F97316' }}>acreditar.</span>
      </h2>
      <div className="flex-1 flex flex-col items-center justify-center gap-5 rounded-2xl"
           style={{ background: 'rgba(249,115,22,0.08)', border: '2px dashed rgba(249,115,22,0.4)' }}>
        <div style={{ fontSize: 64 }}>🏗️</div>
        <div className="font-bold text-xl" style={{ color: '#F97316' }}>Demonstração em Tempo Real</div>
        <div className="text-center text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)', maxWidth: 400 }}>
          Upload de planta → IA analisa → Orçamento completo gerado<br />
          <strong style={{ color: '#F97316' }}>quantocustaobra.com.br</strong>
        </div>
      </div>
    </div>
  );
}

function SlideStats({ slide }) {
  return (
    <div className="w-full h-full flex flex-col p-14" style={{ background: '#0A1628' }}>
      <SlideHeader label={slide.label} white />
      <TitleText parts={slide.title} orangeMap={slide.titleOrange} white />
      <div className="flex-1 grid grid-cols-2 gap-4 mt-6">
        {slide.stats.map((s, i) => (
          <div key={i} className="rounded-2xl p-6 flex flex-col justify-center"
               style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${s.highlight ? 'rgba(249,115,22,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
            <div className="font-black leading-none mb-2"
                 style={{ fontSize: s.highlight ? 'clamp(40px,5vw,64px)' : 28, color: s.highlight ? '#F97316' : 'white', letterSpacing: -2, fontFamily: 'system-ui' }}>
              {s.value}
            </div>
            <div className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideMarket() {
  return (
    <div className="w-full h-full flex flex-col p-14" style={{ background: '#F8F9FA' }}>
      <SlideHeader label="Mercado" />
      <h2 className="font-black leading-tight mb-2" style={{ fontSize: 'clamp(28px,4vw,52px)', letterSpacing: '-1.5px', color: '#1E3A5F', fontFamily: 'system-ui' }}>
        Um mercado <span style={{ color: '#F97316' }}>enorme</span><br />sem solução adequada.
      </h2>
      <div className="flex-1 grid grid-cols-3 gap-5 mt-8" style={{ alignContent: 'start' }}>
        {[
          { value: '1M+', label: 'Obras por ano', desc: 'Novas obras iniciadas no Brasil anualmente' },
          { value: '6,5%', label: 'do PIB brasileiro', desc: 'Participação da construção civil na economia' },
          { value: '18%', label: 'ao ano', desc: 'Crescimento do software para construção civil' },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl p-6 text-center" style={{ background: 'white', border: '1px solid #E9ECEF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div className="font-black mb-2" style={{ fontSize: 48, color: '#F97316', letterSpacing: -2, lineHeight: 1, fontFamily: 'system-ui' }}>{s.value}</div>
            <div className="font-bold mb-1 text-sm" style={{ color: '#1E3A5F' }}>{s.label}</div>
            <div className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>{s.desc}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 p-5 rounded-2xl" style={{ background: 'rgba(30,58,95,0.07)', borderLeft: '4px solid #F97316' }}>
        <div className="font-bold text-sm mb-1" style={{ color: '#1E3A5F' }}>Oportunidade clara</div>
        <div className="text-sm" style={{ color: '#6B7280' }}>
          Nenhum concorrente brasileiro oferece leitura automatizada de plantas com IA e assistente conversacional integrado. A janela está aberta agora.
        </div>
      </div>
    </div>
  );
}

function SlidePricing() {
  const plans = [
    { name: 'Gratuito', price: 'R$ 0', period: 'para começar', features: ['1 orçamento', '2 cômodos visíveis', 'Bruno básico'], highlight: false },
    { name: 'Básico', price: 'R$49,90', period: 'por mês', features: ['10 orçamentos/mês', 'Export PDF', 'Export Excel', 'Base SINAPI'], highlight: false },
    { name: 'Profissional', price: 'R$99,90', period: 'por mês', features: ['Orçamentos ilimitados', 'PDF + Excel', 'Histórico completo', 'Bruno IA completo', 'Acesso API'], highlight: true },
    { name: 'Avulso', price: 'R$69–119', period: 'por análise', features: ['Sem assinatura', 'CUB ou SINAPI', 'PDF profissional'], highlight: false },
  ];

  return (
    <div className="w-full h-full flex flex-col p-14" style={{ background: '#0A1628' }}>
      <SlideHeader label="Modelo de Negócio" white />
      <h2 className="font-black text-white mb-6" style={{ fontSize: 'clamp(24px,3.5vw,46px)', letterSpacing: '-1.5px', fontFamily: 'system-ui' }}>
        Margem acima de <span style={{ color: '#F97316' }}>90%</span>.<br />Custo de IA abaixo de R$1.
      </h2>
      <div className="flex-1 flex gap-4 items-stretch">
        {plans.map((p, i) => (
          <div key={i} className="flex-1 rounded-2xl p-5 flex flex-col"
               style={{ background: p.highlight ? '#F97316' : 'rgba(255,255,255,0.05)', border: `1px solid ${p.highlight ? '#F97316' : 'rgba(255,255,255,0.1)'}` }}>
            <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: p.highlight ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.5)' }}>{p.name}</div>
            <div className="font-black text-2xl text-white leading-none mb-1" style={{ fontFamily: 'system-ui' }}>{p.price}</div>
            <div className="text-xs mb-4" style={{ color: p.highlight ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.45)' }}>{p.period}</div>
            <ul className="space-y-1.5 mt-auto">
              {p.features.map((f, j) => (
                <li key={j} className="flex items-center gap-2 text-xs" style={{ color: p.highlight ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.55)' }}>
                  <span style={{ color: p.highlight ? 'white' : '#F97316', fontWeight: 700 }}>✓</span> {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideCTA() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center relative overflow-hidden"
         style={{ background: '#F97316' }}>
      <div className="absolute inset-0 pointer-events-none opacity-10"
           style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
      <h1 className="font-black text-white leading-tight mb-5 relative"
          style={{ fontSize: 'clamp(40px,8vw,80px)', letterSpacing: '-3px', fontFamily: 'system-ui' }}>
        Vamos construir<br />juntos?
      </h1>
      <p className="text-lg mb-10 relative" style={{ color: 'rgba(255,255,255,0.8)', maxWidth: 520, lineHeight: 1.6 }}>
        Sua audiência já tem a dor que o produto resolve. Estamos abertos a afiliação, co-criação de conteúdo ou sociedade. Queremos ouvir sua visão.
      </p>
      <div className="px-8 py-4 rounded-full bg-white font-black text-lg relative"
           style={{ color: '#F97316', fontFamily: 'system-ui', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        quantocustaobra.com.br
      </div>
      <div className="mt-5 text-sm relative" style={{ color: 'rgba(255,255,255,0.65)' }}>📧 contato@quantocustaobra.com.br</div>
    </div>
  );
}

// ── Renderizador de slide ─────────────────────────────────────
function RenderSlide({ slide }) {
  switch (slide.type) {
    case 'capa':    return <SlideCapa />;
    case 'bruno':   return <SlideBruno />;
    case 'steps':   return <SlideSteps slide={slide} />;
    case 'demo':    return <SlideDemo />;
    case 'stats':   return <SlideStats slide={slide} />;
    case 'market':  return <SlideMarket />;
    case 'pricing': return <SlidePricing />;
    case 'whynow':  return <SlideCards slide={{ ...slide, type: 'light', grid: '2x2' }} />;
    case 'cta':     return <SlideCTA />;
    default:        return <SlideCards slide={slide} />;
  }
}

// ── Componente principal ──────────────────────────────────────
export default function Pitch() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [cur, setCur] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const total = SLIDES.length;

  // Verificar se é admin
  useEffect(() => {
    async function checkAdmin() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { navigate('/login'); return; }

        const { data: profile } = await supabase
          .from('profiles')
          .select('plano')
          .eq('user_id', user.id)
          .single();

        if (profile?.plano === 'admin') {
          setAllowed(true);
        } else {
          navigate('/');
        }
      } catch {
        navigate('/');
      } finally {
        setLoading(false);
      }
    }
    checkAdmin();
  }, [navigate]);

  const goTo = useCallback((n) => {
    setCur((n + total) % total);
    setAnimKey(k => k + 1);
  }, [total]);

  // Teclado + fullscreen
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') goTo(cur + 1);
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')                     goTo(cur - 1);
      if (e.key === 'f' || e.key === 'F') {
        if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
        else document.exitFullscreen?.();
      }
      if (e.key === 'Escape' && !document.fullscreenElement) navigate('/');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cur, goTo, navigate]);

  if (loading) return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ background: '#0F1F33' }}>
      <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#F97316', borderTopColor: 'transparent' }} />
    </div>
  );

  if (!allowed) return null;

  return (
    <div className="fixed inset-0 overflow-hidden select-none" style={{ background: '#0F1F33' }}>

      {/* Slide atual */}
      <div key={animKey} className="w-full h-full"
           style={{ animation: 'pitchFadeIn 0.35s ease' }}>
        <RenderSlide slide={SLIDES[cur]} />
      </div>

      {/* Seta esquerda */}
      <button onClick={() => goTo(cur - 1)}
              className="fixed left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-white transition-all z-50"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}>
        ←
      </button>

      {/* Seta direita */}
      <button onClick={() => goTo(cur + 1)}
              className="fixed right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center text-white transition-all z-50"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}>
        →
      </button>

      {/* Dots */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-50">
        {SLIDES.map((_, i) => (
          <button key={i} onClick={() => goTo(i)}
                  className="rounded-full transition-all"
                  style={{
                    width: i === cur ? 24 : 8,
                    height: 8,
                    background: i === cur ? '#F97316' : 'rgba(255,255,255,0.3)',
                    borderRadius: i === cur ? 4 : '50%',
                  }} />
        ))}
      </div>

      {/* Número do slide */}
      <div className="fixed top-4 right-6 text-xs z-50" style={{ color: 'rgba(255,255,255,0.35)' }}>
        {cur + 1} / {total}
      </div>

      {/* Botão fullscreen */}
      <button onClick={() => {
                if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
                else document.exitFullscreen?.();
              }}
              title="Tela cheia (F)"
              className="fixed top-4 left-4 w-9 h-9 rounded-lg flex items-center justify-center text-sm z-50"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
        ⛶
      </button>

      {/* Botão sair */}
      <button onClick={() => navigate('/')}
              title="Sair (Esc)"
              className="fixed top-4 left-16 w-9 h-9 rounded-lg flex items-center justify-center text-sm z-50"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
        ✕
      </button>

      <style>{`
        @keyframes pitchFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
